import { faker } from '@faker-js/faker'
import type { Order, OrderStatus } from '../../types/order'
import { createProduct } from '../factories/product'

const ORDER_STATUSES: OrderStatus[] = [
  'pending',
  'confirmed',
  'processing',
  'packed',
  'shipped',
  'out_for_delivery',
  'delivered',
  'cancelled',
]

const PAYMENT_METHODS = [
  'Card ending •••• 4242',
  'bKash - 01712345678',
  'Nagad - 01812345678',
  'Cash on Delivery',
  'Rocket - 01912345678',
]

const generateOrderNumber = (): string => {
  const prefix = 'BT'
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = faker.string.alphanumeric(4).toUpperCase()
  return `${prefix}${timestamp}${random}`
}

const getStatusTimeline = (currentStatus: OrderStatus, createdAt: Date) => {
  const timeline: Array<{ status: OrderStatus; timestamp: string; note?: string }> = []
  const statusProgression: OrderStatus[] = [
    'pending',
    'confirmed',
    'processing',
    'packed',
    'shipped',
    'out_for_delivery',
    'delivered',
  ]

  const currentIndex = statusProgression.indexOf(currentStatus)
  if (currentIndex === -1) {
    // Cancelled or refunded
    timeline.push({
      status: 'pending',
      timestamp: createdAt.toISOString(),
      note: 'Order placed successfully',
    })
    timeline.push({
      status: currentStatus,
      timestamp: faker.date.between({ from: createdAt, to: new Date() }).toISOString(),
      note:
        currentStatus === 'cancelled'
          ? 'Order cancelled by customer'
          : 'Order refunded - Amount will be credited in 5-7 business days',
    })
    return timeline
  }

  let currentTime = new Date(createdAt)
  for (let i = 0; i <= currentIndex; i++) {
    const status = statusProgression[i]
    const notes: Record<OrderStatus, string> = {
      pending: 'Order placed successfully',
      confirmed: 'Order confirmed by BotTola',
      processing: 'Order is being prepared',
      packed: 'Order packed and ready for dispatch',
      shipped: 'Order has been dispatched',
      out_for_delivery: 'Order is out for delivery',
      delivered: 'Order delivered successfully',
      cancelled: '',
      refunded: '',
    }

    timeline.push({
      status,
      timestamp: currentTime.toISOString(),
      note: notes[status],
    })

    // Add random time between statuses (30 mins to 3 hours)
    currentTime = new Date(currentTime.getTime() + faker.number.int({ min: 30, max: 180 }) * 60 * 1000)
  }

  return timeline
}

export const generateMockOrder = (overrides?: Partial<Order>): Order => {
  const productCount = faker.number.int({ min: 2, max: 6 })
  const products = Array.from({ length: productCount }, () => createProduct())
  const createdAt = faker.date.recent({ days: 90 })
  const status = (faker.helpers as any).arrayElement(ORDER_STATUSES)

  const items = products.map((product: any) => {
    const quantity = faker.number.int({ min: 1, max: 4 })
    const price = product.current_price
    return {
      id: faker.string.uuid(),
      product_id: product.id,
      title: product.title,
      image: product.images[0],
      price,
      original_price: product.original_price,
      quantity,
      subtotal: price * quantity,
      vendor_name: 'BotTola',
    }
  })

  const subtotal = items.reduce((sum: number, item: any) => sum + item.subtotal, 0)
  const shipping_cost = subtotal >= 500 ? 0 : 50
  const discount = faker.datatype.boolean() ? faker.number.int({ min: 50, max: 200 }) : 0
  const tax = 0
  const total = subtotal + shipping_cost - discount + tax

  return {
    id: faker.string.uuid(),
    order_number: generateOrderNumber(),
    user_id: faker.string.uuid(),
    items,
    status,
    timeline: getStatusTimeline(status, createdAt),
    subtotal,
    shipping_cost,
    discount,
    tax,
    total,
    delivery_address: {
      id: faker.string.uuid(),
      label: (faker.helpers as any).arrayElement(['Home', 'Office', 'Other']),
      full_name: faker.person.fullName(),
      address_line1: faker.location.streetAddress(),
      area: faker.location.county(),
      city: 'Dhaka',
      postal_code: faker.location.zipCode(),
      phone: faker.phone.number(),
      is_default: true,
      created_at: createdAt.toISOString(),
    },
    delivery_slot: {
      id: faker.string.uuid(),
      date: createdAt.toISOString().split('T')[0],
      time_start: '09:00',
      time_end: '11:00',
      label: 'Morning (9 AM - 11 AM)',
      is_available: true,
    },
    payment_method: (faker.helpers as any).arrayElement(PAYMENT_METHODS),
    payment_status:
      status === 'cancelled' ? 'failed' : status === 'refunded' ? 'refunded' : 'paid',
    coupon_code: discount > 0 ? (faker.helpers as any).arrayElement(['WELCOME10', 'SAVE20', undefined]) : undefined,
    notes: faker.datatype.boolean() ? faker.lorem.sentence() : undefined,
    created_at: createdAt.toISOString(),
    updated_at: faker.date.between({ from: createdAt, to: new Date() }).toISOString(),
    ...overrides,
  }
}

export const generateMockOrders = (count: number = 10): Order[] => {
  return Array.from({ length: count }, () => generateMockOrder())
}
